import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useRequests } from '../../context/RequestsContext';
import { useNotifications } from '../../context/NotificationsContext';
import { Moon, Sun, ChevronDown, LogOut, User as UserIcon, Bell } from 'lucide-react';

const REV360_LOGO_SRC = '/Rev360_Light.png';
const NRS_LOGO_SRC = '/logo.png';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const LayoutContainer = styled.div<{ theme: any; $bare?: boolean }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-image: ${props => (props.$bare ? 'none' : "url('/white-bg.jpg')")};
  background-size: cover;
  background-position: unset;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-family: 'Raleway', sans-serif;
  transition: all 0.3s ease;
  position: relative;

  /* Add overlay for better text readability */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props =>
      props.$bare
        ? 'transparent'
        : (props.theme.isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.3)')};
    z-index: 0;
    pointer-events: none;
    transition: background-color 0.3s ease;
  }
`;

const Header = styled.header<{ theme: any }>`
  background: transparent;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 100;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BrandLogos = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
`;

const BrandLogo = styled.img<{ $variant: 'rev' | 'nrs' }>`
  width: auto;
  height: ${({ $variant }) => ($variant === 'rev' ? '42px' : '28px')};
`;

const BrandPipe = styled.span`
  width: 1px;
  height: 28px;
  background: rgba(107, 114, 128, 0.55);
  display: inline-block;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: 1rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavLinkItem = styled.li``;

const NavLinkStyled = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  text-decoration: none;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.surface};
  }
`;

const RequestsBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  animation: ${pulse} 2s infinite;
`;

const ProfileContainer = styled.div`
  position: relative;
`;

const NotifContainer = styled.div`
  position: relative;
`;

const ProfileButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 999px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.surface};
  }
`;

const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #973031 100%);
  font-size: 0.8rem;
  font-weight: 700;
`;

const ProfileMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 180px;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  padding: 0.5rem;
  z-index: 200;
`;

const ProfileMenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  text-align: left;
  padding: 0.5rem 0.625rem;
  border-radius: 8px;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const NotificationButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', '$hasUnread'].includes(prop)
})<{ theme: any; $hasUnread: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
  
  &::after {
    display: ${props => (props.$hasUnread ? 'block' : 'none')};
    content: '';
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    animation: ${pulse} 2s infinite;
  }
`;

const NotificationsMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 360px;
  max-width: calc(100vw - 2rem);
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0,0,0,0.14);
  overflow: hidden;
  z-index: 220;
`;

const NotificationsHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  font-weight: 800;
`;

const NotificationsList = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

const NotificationItem = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', '$unread'].includes(prop),
})<{ theme: any; $unread: boolean }>`
  width: 100%;
  border: none;
  background: ${p => (p.$unread ? `${p.theme.colors.primary}10` : 'transparent')};
  padding: 0.85rem 1rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  gap: 0.75rem;
  transition: background 0.15s ease;
  color: ${p => p.theme.colors.text};

  &:hover {
    background: ${p => `${p.theme.colors.primary}14`};
  }
`;

const NotifDot = styled.span<{ theme: any; $unread: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => (p.$unread ? '#ef4444' : p.theme.colors.border)};
  margin-top: 0.35rem;
  flex-shrink: 0;
`;

const NotifText = styled.div`
  min-width: 0;
  flex: 1;
`;

const NotifTitle = styled.div<{ theme: any }>`
  font-weight: 800;
  font-size: 0.92rem;
  color: ${p => p.theme.colors.text};
  margin-bottom: 0.25rem;
`;

const NotifBody = styled.div<{ theme: any }>`
  font-weight: 600;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.textSecondary};
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NotifTime = styled.div<{ theme: any }>`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 700;
`;

const NotificationsFooter = styled.div<{ theme: any }>`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ModalOverlay = styled.div<{ theme: any }>`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 400;
`;

const ModalCard = styled.div<{ theme: any }>`
  width: 640px;
  max-width: 100%;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(0,0,0,0.22);
  overflow: hidden;
`;

const ModalHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ModalTitle = styled.div<{ theme: any }>`
  font-weight: 900;
  color: ${p => p.theme.colors.text};
  font-size: 1rem;
`;

const ModalSub = styled.div<{ theme: any }>`
  margin-top: 0.3rem;
  font-weight: 700;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.textSecondary};
`;

const ModalClose = styled.button<{ theme: any }>`
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  border-radius: 10px;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  font-weight: 900;
  line-height: 1;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: 1rem 1.1rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0.6rem 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DetailLabel = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const DetailValue = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 700;
  word-break: break-word;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div<{ theme: any }>`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.9rem 1.1rem;
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const ModalAction = styled.button<{ theme: any; $primary?: boolean }>`
  border: 1px solid ${p => (p.$primary ? p.theme.colors.primary : p.theme.colors.border)};
  background: ${p => (p.$primary ? p.theme.colors.primary : 'transparent')};
  color: ${p => (p.$primary ? 'white' : p.theme.colors.text)};
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
  cursor: pointer;
  font-weight: 900;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ThemeToggle = styled.button<{ theme: any }>`
  background: none;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const Main = styled.main`
  flex: 1;
  position: relative;
  z-index: 10;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Footer = styled.footer<{ theme: any }>`
  margin-top: auto;
  padding: 2rem 0;
  text-align: center;
  border-top: 1px solid ${props => props.theme.colors.border}40;
  animation: ${fadeInUp} 1s ease-out 1.4s both;
  position: relative;
  z-index: 1;
`;

const Copyright = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  margin: 0;
  font-weight: 400;
`;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user, consultant, currentUser, logout } = useAuth();
  const { pendingRequestsCount } = useRequests();
  const { notifications, unreadCount, isLoading: isLoadingNotifs, refresh, markRead, markAllRead } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifDetailOpen, setNotifDetailOpen] = useState(false);
  const [notifDetail, setNotifDetail] = useState<any | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const isLanding = location.pathname === '/';

  useEffect(() => {
    setMenuOpen(false);
    setNotifOpen(false);
    setNotifDetailOpen(false);
    setNotifDetail(null);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNotifDetailOpen(false);
        setNotifDetail(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const getInitials = (fullName?: string) => {
    const base = fullName || consultant?.firmName || user?.profile?.email || 'U';
    return base.split(' ').map((s: string) => s[0]).join('').toUpperCase().slice(0, 2);
  };

  const isOwnerRole = (() => {
    const roleFromMe = currentUser?.role;
    if (typeof roleFromMe === 'string' && roleFromMe.trim()) {
      return roleFromMe.trim().toUpperCase() === 'OWNER';
    }

    // Fallback to token claims if /me hasn't loaded yet
    const p: any = user?.profile || {};
    const direct = [
      p?.role,
      p?.teamRole,
      p?.team_role,
      p?.userRole,
      p?.user_role,
      p?.portalRole,
      p?.portal_role,
    ].find((v) => typeof v === 'string' && v.trim());
    if (direct) return String(direct).toUpperCase() === 'OWNER';
    const roles = Array.isArray(p?.roles) ? p.roles : null;
    if (roles && roles.some((r: any) => String(r).toUpperCase() === 'OWNER')) return true;
    return false;
  })();

  const formatNotifTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
  };

  const renderNotifTitle = (n: any) => {
    if (n?.type === 'NEW_REQUEST') return 'New client request';
    return n?.type || 'Notification';
  };

  const renderNotifBody = (n: any) => {
    const p = n?.payload || {};
    if (n?.type === 'NEW_REQUEST') {
      const taxId = p?.taxId ? `Tax ID: ${p.taxId}` : '';
      const from = p?.requestorEmail ? `From: ${p.requestorEmail}` : '';
      return [taxId, from].filter(Boolean).join(' · ') || 'A new request was received.';
    }
    if (typeof p?.message === 'string' && p.message.trim()) return p.message.trim();
    return 'You have a new notification.';
  };

  const openNotifDetail = (n: any) => {
    setNotifDetail(n);
    setNotifDetailOpen(true);
  };

  return (
    <LayoutContainer theme={theme} $bare={isLanding}>
      {!isLanding && (
        <Header theme={theme}>
          <Logo to="/">
            <BrandLogos>
              <BrandLogo $variant="rev" src={REV360_LOGO_SRC} alt="Rev360" />
              <BrandPipe aria-hidden="true" />
              <BrandLogo $variant="nrs" src={NRS_LOGO_SRC} alt="Nigeria Revenue Service" />
            </BrandLogos>
          </Logo>
          <RightActions>
                 {isAuthenticated && (
                   <Nav>
                     <NavList>
                       <NavLinkItem>
                         <NavLinkStyled to="/dashboard" theme={theme}>Dashboard</NavLinkStyled>
                       </NavLinkItem>
                       <NavLinkItem>
                         <NavLinkStyled to="/clients" theme={theme}>Clients</NavLinkStyled>
                       </NavLinkItem>
                       <NavLinkItem>
                        {isOwnerRole && (
                          <NavLinkStyled to="/requests" theme={theme}>
                            Requests
                            {pendingRequestsCount > 0 && (
                              <RequestsBadge theme={theme}>
                                {pendingRequestsCount}
                              </RequestsBadge>
                            )}
                          </NavLinkStyled>
                        )}
                       </NavLinkItem>
                       <NavLinkItem>
                         <NavLinkStyled to="/calendar" theme={theme}>Calendar</NavLinkStyled>
                       </NavLinkItem>
                     </NavList>
                   </Nav>
                 )}

            {isAuthenticated && (
              <NotifContainer ref={notifRef}>
                <NotificationButton
                  theme={theme}
                  $hasUnread={unreadCount > 0}
                  onClick={async () => {
                    const next = !notifOpen;
                    setNotifOpen(next);
                    if (next) await refresh({ limit: 20 });
                  }}
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                </NotificationButton>

                {notifOpen && (
                  <NotificationsMenu theme={theme}>
                    <NotificationsHeader theme={theme}>
                      <span>Notifications</span>
                      <span style={{ color: theme.colors.textSecondary, fontWeight: 800, fontSize: '0.85rem' }}>
                        {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                      </span>
                    </NotificationsHeader>

                    <NotificationsList>
                      {isLoadingNotifs && (
                        <div style={{ padding: '1rem', color: theme.colors.textSecondary, fontWeight: 600 }}>
                          Loading...
                        </div>
                      )}
                      {!isLoadingNotifs && notifications.length === 0 && (
                        <div style={{ padding: '1rem', color: theme.colors.textSecondary, fontWeight: 600 }}>
                          No notifications yet.
                        </div>
                      )}
                      {!isLoadingNotifs && notifications.map((n: any) => {
                        const unread = !n.readAt;
                        return (
                          <NotificationItem
                            key={n.id}
                            theme={theme}
                            $unread={unread}
                            onClick={async () => {
                              if (unread) {
                                try { await markRead(n.id); } catch {}
                              }
                              openNotifDetail(n);
                            }}
                          >
                            <NotifDot theme={theme} $unread={unread} />
                            <NotifText>
                              <NotifTitle theme={theme}>{renderNotifTitle(n)}</NotifTitle>
                              <NotifBody theme={theme}>{renderNotifBody(n)}</NotifBody>
                              <NotifTime theme={theme}>{formatNotifTime(n.createdAt)}</NotifTime>
                            </NotifText>
                          </NotificationItem>
                        );
                      })}
                    </NotificationsList>

                    <NotificationsFooter theme={theme}>
                      <button
                        onClick={async () => {
                          try { await markAllRead(); } catch {}
                        }}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${theme.colors.border}`,
                          color: theme.colors.text,
                          borderRadius: 10,
                          padding: '0.5rem 0.75rem',
                          fontWeight: 800,
                          cursor: unreadCount === 0 ? 'not-allowed' : 'pointer',
                          opacity: unreadCount === 0 ? 0.6 : 1,
                        }}
                        disabled={unreadCount === 0}
                      >
                        Mark all read
                      </button>
                    </NotificationsFooter>
                  </NotificationsMenu>
                )}
              </NotifContainer>
            )}

            {isAuthenticated && (
              <ProfileContainer ref={menuRef}>
                <ProfileButton theme={theme} onClick={() => setMenuOpen(v => !v)}>
                  <Avatar theme={theme}>{getInitials(consultant?.firmName || user?.profile?.name)}</Avatar>
                  <ChevronDown size={16} />
                </ProfileButton>
                {menuOpen && (
                  <ProfileMenu theme={theme}>
                    <ProfileMenuItem theme={theme} as={Link} to="/profile">
                      <UserIcon size={16} /> Profile
                    </ProfileMenuItem>
                    {isOwnerRole && (
                      <ProfileMenuItem theme={theme} as={Link} to="/teams">
                        <UserIcon size={16} /> Team
                      </ProfileMenuItem>
                    )}
                    <ProfileMenuItem theme={theme} onClick={logout}>
                      <LogOut size={16} /> Logout
                    </ProfileMenuItem>
                  </ProfileMenu>
                )}
              </ProfileContainer>
            )}

            <ThemeToggle theme={theme} onClick={toggleTheme}>
              {theme.isDark ? <Sun size={17} /> : <Moon size={17} />}
            </ThemeToggle>
          </RightActions>
        </Header>
      )}

      {notifDetailOpen && notifDetail && (
        <ModalOverlay
          theme={theme}
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setNotifDetailOpen(false);
              setNotifDetail(null);
            }
          }}
        >
          <ModalCard theme={theme} onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <div>
                <ModalTitle theme={theme}>{renderNotifTitle(notifDetail)}</ModalTitle>
                <ModalSub theme={theme}>
                  {formatNotifTime(notifDetail.createdAt)} {notifDetail.readAt ? '· Read' : '· Unread'}
                </ModalSub>
              </div>
              <ModalClose
                theme={theme}
                onClick={() => {
                  setNotifDetailOpen(false);
                  setNotifDetail(null);
                }}
                aria-label="Close"
              >
                ✕
              </ModalClose>
            </ModalHeader>

            <ModalBody>
              <DetailGrid>
                <DetailLabel theme={theme}>Summary</DetailLabel>
                <DetailValue theme={theme}>{renderNotifBody(notifDetail)}</DetailValue>

                {notifDetail?.type === 'NEW_REQUEST' && (
                  <>
                    <DetailLabel theme={theme}>Tax ID</DetailLabel>
                    <DetailValue theme={theme}>{String(notifDetail?.payload?.taxId ?? '—')}</DetailValue>

                    <DetailLabel theme={theme}>Requestor Email</DetailLabel>
                    <DetailValue theme={theme}>{String(notifDetail?.payload?.requestorEmail ?? '—')}</DetailValue>

                    <DetailLabel theme={theme}>Expected Deadline</DetailLabel>
                    <DetailValue theme={theme}>
                      {notifDetail?.payload?.expectedDeadline
                        ? formatNotifTime(String(notifDetail.payload.expectedDeadline))
                        : '—'}
                    </DetailValue>

                    <DetailLabel theme={theme}>Message</DetailLabel>
                    <DetailValue theme={theme}>{String(notifDetail?.payload?.message ?? '—')}</DetailValue>
                  </>
                )}
              </DetailGrid>
            </ModalBody>

            <ModalFooter theme={theme}>
              {notifDetail?.type === 'NEW_REQUEST' && isOwnerRole && (
                <ModalAction
                  theme={theme}
                  $primary
                  onClick={() => {
                    setNotifDetailOpen(false);
                    setNotifDetail(null);
                    navigate('/requests');
                  }}
                >
                  View Requests
                </ModalAction>
              )}
              <ModalAction
                theme={theme}
                onClick={() => {
                  setNotifDetailOpen(false);
                  setNotifDetail(null);
                }}
              >
                Close
              </ModalAction>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      <Main>
        {children}
      </Main>

      {!isLanding && (
        <Footer theme={theme}>
          <Copyright theme={theme}>
            © {new Date().getFullYear()} Nigeria Revenue Service. All rights reserved.
          </Copyright>
        </Footer>
      )}
    </LayoutContainer>
  );
};

export default Layout;
